import type { PropsWithChildren } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { ActivityTrackerProvider as TrackerProvider } from '@vsaitech/activity-tracker/react'

/** Identifier for this frontend in the activity lake (events.client_id). */
const CLIENT_ID = 'asset-insight-recorder'

const COLLECTOR_URL = import.meta.env.VITE_ACTIVITY_COLLECTOR_URL

/**
 * Page-level active-stay-time tracking via @vsaitech/activity-tracker.
 *
 * Thin adapter over the SDK's own provider: it supplies the collector URL from
 * the environment and the user id from Auth0, and owns no tracker lifecycle of
 * its own. The SDK starts one tracker per mount and re-points it at the new
 * user in place when `userId` changes, so login/logout (and Auth0's silent
 * renewals) no longer discard buffered records. Records are stamped with the
 * user id at enqueue time, so there is no need to wait for `isLoading` — the
 * first record is written long after auth has resolved.
 *
 * Inert unless VITE_ACTIVITY_COLLECTOR_URL is set, so local/test (and any env
 * without a reachable collector) never call out. Internal/staff traffic is
 * excluded server-side by the collector's internal-IP check, so there is no
 * client-side email gating here. No-op when disabled — safe to mount everywhere.
 */
export function ActivityTrackerProvider({ children }: PropsWithChildren): JSX.Element {
  const { user } = useAuth0()

  if (!COLLECTOR_URL) return <>{children}</>

  return (
    <TrackerProvider
      collectorUrl={COLLECTOR_URL}
      clientId={CLIENT_ID}
      userId={user?.sub}
      appVersion={import.meta.env.VITE_APP_VERSION}
    >
      {children}
    </TrackerProvider>
  )
}
